import { DotLink, ParsleyLockup, PillButton } from '@parsleyai/ui'
import { ArrowUp } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import FeedbackDialog from '@/components/feedback/FeedbackDialog'
import { useWhatsNewIndicator } from '@/pages/whatsNew/useWhatsNewIndicator'

// The in-app footer: a two-tier utility footer, desktop-only (its PageLayout mount is `hidden md:block`).
// Deep green in light, black in dark — its chrome uses fixed ramp values that read on both, not flipping tokens.

// Muted link that brightens to white on hover, on the green-tinted `gray-*` ramp (the saturated
// `brand-green` ramp can't hit this low-chroma olive). WHITE focus ring: pelorous is ~2:1 on this dark surface.
const LINK_CLASS =
  'rounded-sm text-gray-300 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-green-900 dark:focus-visible:ring-offset-black'

function scrollToTop() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
}

const Footer: React.FC = () => {
  const { t } = useTranslation()
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const { unseen } = useWhatsNewIndicator()
  const version = `v${import.meta.env.VITE_APP_VERSION}`

  return (
    <footer className="w-full bg-brand-green-800 dark:bg-black">
      <div className="mx-auto max-w-page px-11">
        {/* Tier 1 — utility row */}
        <div className="flex flex-wrap items-center justify-between gap-6 pb-6 pt-9">
          <div className="flex items-center gap-3">
            <ParsleyLockup variant="bare" tone="inverse" size="sm" />
            <span className="ml-2 inline-flex items-center rounded-full border border-white/[0.14] bg-white/[0.06] px-3.5 py-1.5 font-mono text-gray-300 text-xs tracking-[0.02em]">
              {t('footer.betaPill')}
            </span>
          </div>

          <nav className="flex items-center gap-6 text-sm" aria-label={t('footer.utilitiesLabel')}>
            <Link to="/settings/help" className={LINK_CLASS}>
              {t('footer.helpCenter')}
            </Link>
            <button type="button" onClick={() => setFeedbackOpen(true)} className={LINK_CLASS}>
              {t('footer.sendFeedback')}
            </button>
            <DotLink asChild dot={unseen} dotLabel={t('whatsNew.unseenLabel')} className={LINK_CLASS}>
              <Link to="/whats-new">{t('footer.whatsNew')}</Link>
            </DotLink>
          </nav>
        </div>

        {/* Tier 2 — bottom bar */}
        <div className="flex flex-wrap items-center justify-between gap-5 border-t border-white/[0.13] pb-10 pt-5">
          {/* gray-500 -> gray-400: at 12px this is not large text, so it needs 4.5:1, and gray-500
              was 4.01:1 on the deep green (it passed on the dark-mode black at 6.27:1, so this was
              a light-mode-only failure). gray-400 is 6.17:1. It now matches the links' colour, but
              stays visibly quieter than them through its smaller size and mono face rather than
              through a contrast level that fails. */}
          <p className="flex flex-wrap items-center gap-2.5 font-mono text-xs tracking-[0.02em] text-gray-300">
            <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
            <span className="opacity-50">·</span>
            <span>{t('common.version', { version })}</span>
          </p>

          <nav className="flex items-center gap-5 text-sm" aria-label={t('footer.legalLabel')}>
            <a href="https://parsleycooking.com/privacy/" target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
              {t('footer.privacy')}
            </a>
            <a href="https://parsleycooking.com/terms/" target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
              {t('footer.terms')}
            </a>
            <a href="https://parsleycooking.com/cookie-policy/" target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
              {t('footer.cookies')}
            </a>
          </nav>

          <PillButton
            type="button"
            variant="on-green-secondary"
            onClick={scrollToTop}
            className="group"
            trailing={<ArrowUp className="transition-transform group-hover:-translate-y-0.5" aria-hidden="true" />}>
            {t('footer.backToTop')}
          </PillButton>
        </div>
      </div>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </footer>
  )
}

export default Footer
